import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { waLink } from "@/lib/brand";

const groups = [
  {
    id: "all-subjects",
    badge: "Best Value · Most Popular",
    featured: true,
    title: "Complete All-Subjects Package",
    lead: "One flat fee. Every subject covered. Every extra included. A special bundle offer for students who want a truly complete learning experience.",
    subjects: ["Mathematics", "Science (Phy · Chem · Bio)", "English", "Social Studies", "Hindi", "Computer Science"],
    highlights: [
      "All main subjects in one plan",
      "Weekly + monthly tests included",
      "Printed chapter-wise notes & sample papers",
      "Saturday doubt-solving sessions",
      "Personal mentoring & study plan",
      "Monthly progress reports to parents",
    ],
    duration: "Full academic session",
    batch: "Available for Class VI to X",
    fee: "₹2,500 / month · all subjects",
    cta: "Enroll Now",
  },
  {
    id: "foundation",
    badge: "Class VI · VII · VIII",
    title: "Foundation Batch",
    lead: "Concept-first teaching for young minds — where every strong topper story begins.",
    subjects: ["Mathematics", "Science (Phy · Chem · Bio)", "English", "Social Studies (Hist · Geo · Civ)", "Hindi", "Computer Science"],
    highlights: ["Concept-first teaching", "Weekly tests", "Homework support", "Parent-teacher meetings"],
    duration: "Full academic session",
    batch: "Limited to 15 students",
    fee: "₹999 / subject / month",
    cta: "Enroll Now",
  },
  {
    id: "ix",
    badge: "Class IX",
    title: "Full Course",
    lead: "The year your child transitions from school to board-level rigor. We prepare them for it.",
    subjects: ["Maths", "Physics", "Chemistry", "Biology", "English", "Social Studies", "Hindi"],
    highlights: ["NCERT + reference book coverage", "Weekly chapter tests + monthly cumulative", "Doubt-solving every Saturday", "Personal mentoring"],
    duration: "12 months",
    batch: "Small batch · early enrollment recommended",
    fee: "₹999 / subject / month",
    cta: "Join Today",
  },
  {
    id: "x",
    badge: "Class X · CBSE & ICSE",
    title: "Board Batch",
    lead: "10 months of focused, board-oriented preparation with mock papers, PYQs and pre-boards.",
    subjects: ["All main subjects (board-focused)", "Answer-writing techniques", "Sample paper drills"],
    highlights: ["Chapter-wise + full-syllabus mock tests", "Previous year papers (last 10 years)", "Pre-board simulation", "Detailed monthly reports"],
    duration: "10 months (April – February)",
    batch: "Small batch of Class X students",
    fee: "₹999 / subject / month",
    cta: "Enroll Now",
  },
  {
    id: "crash",
    badge: "Crash Course & Revision",
    title: "Fast-Track Revision Batch",
    lead: "Perfect for Class 9 & 10 students who need intensive revision before final exams.",
    subjects: ["Chapter summaries", "Formula sheets", "Daily MCQs + weekly mock papers"],
    highlights: ["8–10 week intensive", "Focused doubt-solving", "Board-style paper practice"],
    duration: "8 – 10 weeks",
    batch: "Class 9 & 10 students",
    fee: "Contact for fees",
    cta: "Book Free Demo",
  },
];

export default function CoursesPage() {
  return (
    <div>
      <section className="bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="vk-chip">Courses</span>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl font-800 tracking-tight max-w-3xl">Every subject. Every class. Every board.</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/70">
            Complete academic support for Class VI to X — CBSE and ICSE — across every core subject.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="vk-btn-primary">Book Free Demo <ArrowRight size={16} /></Link>
            <a href={waLink()} target="_blank" rel="noreferrer" className="vk-btn-outline">WhatsApp Now</a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {groups.map((g,i)=>(
            <div key={g.id} id={g.id} data-testid={`course-${g.id}`} className={`grid lg:grid-cols-12 gap-8 items-start ${g.featured ? "relative rounded-2xl p-8 lg:p-10 bg-gradient-to-br from-[#FF6B35]/5 via-white to-white border-2 border-[#FF6B35]" : ""}`}>
              {g.featured && <span className="absolute -top-4 left-1/2 -translate-x-1/2 lg:left-10 lg:translate-x-0 text-xs font-bold uppercase tracking-widest bg-[#FF6B35] text-white px-4 py-1.5 rounded-full shadow-lg">★ Most Popular</span>}
              <div className="lg:col-span-5">
                <span className="vk-chip">{g.badge}</span>
                <h2 className="mt-4 font-display text-4xl sm:text-5xl font-800 tracking-tight text-[#0B192C]">{g.title}</h2>
                <p className="mt-4 text-slate-600 text-lg">{g.lead}</p>
                <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Duration</div>
                    <div className="mt-1 font-medium text-[#0F172A]">{g.duration}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Batch</div>
                    <div className="mt-1 font-medium text-[#0F172A]">{g.batch}</div>
                  </div>
                </div>
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Fee</div>
                  <div className="mt-1 font-display text-3xl font-800 text-[#FF6B35]">{g.fee}</div>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/contact" data-testid={`enroll-${g.id}`} className="vk-btn-primary">{g.cta} <ArrowRight size={16} /></Link>
                  <a href={waLink(`Hi, I want details on the ${g.title} for ${g.badge}.`)} target="_blank" rel="noreferrer" className="vk-btn-dark">WhatsApp</a>
                </div>
              </div>
              <div className="lg:col-span-7 rounded-lg border border-slate-200 bg-[#F5F7FA] p-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Subjects</div>
                    <ul className="mt-3 space-y-2">
                      {g.subjects.map((s)=>(
                        <li key={s} className="flex gap-2 text-sm text-slate-800"><Check size={16} className="text-[#FF6B35] mt-0.5" /> {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Highlights</div>
                    <ul className="mt-3 space-y-2">
                      {g.highlights.map((s)=>(
                        <li key={s} className="flex gap-2 text-sm text-slate-800"><Check size={16} className="text-[#FF6B35] mt-0.5" /> {s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl font-800">Not sure which batch fits your child?</h2>
          <p className="mt-3 text-white/70">Book a free demo — our team will help you decide.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="vk-btn-primary">Book Free Demo <ArrowRight size={16} /></Link>
            <a href={waLink()} target="_blank" rel="noreferrer" className="vk-btn-outline">WhatsApp Now</a>
          </div>
        </div>
      </section>
    </div>
  );
}
